import React from 'react';
import './SmartActionBar.css';
import './SmartActionBar.glass.css';

export interface SmartActionBarProps {
  completedCount: number;
  totalCount: number;
  submissionCount: number;
  unreadMessageCount: number;
  redoCount: number;
  expanded?: boolean;
  onToggle?: () => void;
  /** Opens conversations inbox */
  onViewMessages?: () => void;
}

type BarMode = 'empty' | 'redo' | 'messages' | 'all-done' | 'default';

function resolveMode(props: SmartActionBarProps): BarMode {
  if (props.redoCount > 0) return 'redo';
  if (props.unreadMessageCount > 0) return 'messages';
  if (props.completedCount > 0 && props.completedCount === props.totalCount) return 'all-done';
  // Pre-submission empty state: tasks exist but nothing's happened yet.
  // Surfaces the dock from the very first visit as the canonical task strip.
  if (props.completedCount === 0 && props.submissionCount === 0 && props.totalCount > 0) return 'empty';
  return 'default';
}

const ICON: Record<BarMode, React.ReactNode> = {
  redo: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M1 4v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  ),
  messages: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  'all-done': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  ),
  default: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  ),
  // Empty mode — small task-stack icon (3 stacked lines, "to-do list" feel).
  empty: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="20" y2="12" />
      <line x1="8" y1="18" x2="20" y2="18" />
      <circle cx="4" cy="6" r="1.2" fill="currentColor" />
      <circle cx="4" cy="12" r="1.2" fill="currentColor" />
      <circle cx="4" cy="18" r="1.2" fill="currentColor" />
    </svg>
  ),
};

export const SmartActionBar: React.FC<SmartActionBarProps> = (props) => {
  const { completedCount, totalCount, submissionCount, unreadMessageCount, redoCount, expanded, onToggle, onViewMessages } = props;
  const mode = resolveMode(props);

  // No tasks at all → no dock. (Empty mode still renders when totalCount > 0.)
  if (totalCount === 0 && unreadMessageCount === 0 && redoCount === 0) return null;

  const remaining = totalCount - completedCount;
  let onTap: (() => void) | undefined = onToggle;
  let labelContent: React.ReactNode;

  switch (mode) {
    case 'empty':
      // No expand affordance — drawer is empty pre-submission.
      onTap = undefined;
      labelContent = (
        <>
          <span className="sab-count">{totalCount}</span>
          <span className="sab-count-label">{totalCount === 1 ? 'task' : 'tasks'}</span>
        </>
      );
      break;
    case 'redo':
      labelContent = (
        <>
          <span className="sab-count">{redoCount}</span>
          <span className="sab-count-label">{redoCount === 1 ? 'task needs redo' : 'tasks need redo'}</span>
        </>
      );
      break;
    case 'messages':
      onTap = onViewMessages || onToggle;
      labelContent = (
        <>
          <span className="sab-count">{unreadMessageCount}</span>
          <span className="sab-count-label">{unreadMessageCount === 1 ? 'new message' : 'new messages'}</span>
        </>
      );
      break;
    case 'all-done':
      labelContent = (
        <>
          <span className="sab-count">{submissionCount}</span>
          <span className="sab-count-label">submitted</span>
          <span className="sab-separator">&middot;</span>
          <span className="sab-remaining">All done</span>
        </>
      );
      break;
    default:
      labelContent = (
        <>
          <span className="sab-count">{submissionCount}</span>
          <span className="sab-count-label">submitted</span>
          {remaining > 0 && (
            <>
              <span className="sab-separator">&middot;</span>
              <span className="sab-remaining">{remaining} remaining</span>
            </>
          )}
        </>
      );
  }

  const progress = totalCount > 0 ? completedCount / totalCount : 0;
  const allDone = completedCount === totalCount && totalCount > 0;
  const ringRadius = 10;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference * (1 - progress);

  const isEmpty = mode === 'empty';

  return (
    <button
      className={`sab-bar sab-${mode} ${expanded ? 'sab-expanded' : ''}`}
      onClick={onTap}
      aria-label={isEmpty ? `${totalCount} tasks` : `${submissionCount} submitted`}
      aria-expanded={expanded}
      disabled={isEmpty}
    >
      <span className="sab-icon">{ICON[mode]}</span>
      <span className="sab-label">{labelContent}</span>
      {!isEmpty && (
        <>
          <svg className="sab-ring" width="26" height="26" viewBox="0 0 26 26">
            <circle cx="13" cy="13" r={ringRadius} fill="none" stroke="var(--border, #E8E4DF)" strokeWidth="2.5" />
            <circle
              cx="13" cy="13" r={ringRadius}
              fill="none"
              stroke={allDone ? 'var(--sage, #6E7A5A)' : 'var(--ember, #D4715A)'}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringOffset}
              transform="rotate(-90 13 13)"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <svg className="sab-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </>
      )}
    </button>
  );
};
